import {getBuildingPrice} from "../../../utils/economy";

import {createUnit, getUnitMaxSizeForFaction, getUnitTypeConfig, UNIT_TYPES} from "../../../configs/units";
import {createPlayersState} from "../../../utils/gameState/createPlayersState";
import {checkIfPlayerBonusPerCityRule} from "../../../utils/gameState/checkIfPlayerBonusPerCityRule";
import {PHASES, ROUND_PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {CONSTRUCTION_ACTION_TYPES} from "../../../constants/construction";
import {
  countWorkingPalaces,
  getConstructionActionsSources,
  sumConstructionActionsSources
} from "../../../utils/gameState/construction/getConstructionActionsSources";
import {COINS_TO_WIN_POINTS_CONVERSION, FINAL_ROUND, GAME_END_WIN_POINTS_GAP} from "../../../configs/game";
import {getNeutralPlayerMock} from "../../../utils/players/getNeutralPlayerMock";
import {SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {POLICY_AGRARIANISM_BUILDING_PRICE_FARM} from "../../../configs/policy/agrarianism";
import {checkIfUnitUpgradeUndoExceedsLimit} from "../../../utils/gameState/recruitment/checkIfUnitUpgradeUndoExceedsLimit";
import {checkIfUnitTypeChangeExceedsLimit} from "../../../utils/gameState/recruitment/checkIfUnitTypeChangeExceedsLimit";
import {GAME_END_REASONS, GAME_TYPES} from "../../../constants/game";
import {findOwner} from "../../../utils/gameState/findOwner";
import {checkIfFortressConstructionBlocked} from "../../../utils/gameState/construction/checkIfFortressConstructionBlocked";
import {calcPolicyPrice} from "../../../utils/policy/calcPolicyPrice";
import {getArrayWithoutIndex} from "../../../utils/array/getArrayWithoutIndex";
import {isPlayerFaction} from "../../../utils/factions/isPlayerFaction";
import {isUnitFaction} from "../../../utils/factions/isUnitFaction";
import {FACTIONS} from "../../../constants/factions";
import {
  checkIfHasRemainingConstructionActions
} from "../../../utils/gameState/construction/checkIfHasRemainingConstructionActions";
import {isPlayerPolicy} from "../../../utils/factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {POLICY_INDUSTRIALISM_CONSTRUCTION_ACTIONS_BONUS} from "../../../configs/policy/industrialism";
import {POLICY_FORTIFICATIONISM_FORTRESS_ROUND_END_REPAIRMENT} from "../../../configs/policy/fortificationism";
import {
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D10,
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D12,
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D20,
  POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D8
} from "../../../configs/policy/totalitarianism";
import {
  POLICY_FANATICISM_COINS_CONVERSION,
  POLICY_FANATICISM_WIN_POINTS_CONVERSION
} from "../../../configs/policy/fanaticism";
import {calcAgrarianismConversion} from "../../../utils/policy/agrarianism/calcAgrarianismConversion";
import {SUCCUBES_SEDUCTION_COST} from "../../../configs/factions/succubes";
import {
  GOBLINS_ABILITY_RADIUS,
  GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY,
  GOBLINS_TRAP_REMOVAL_REWARD,
  GOBLINS_TRAP_SETUP_COST
} from "../../../configs/factions/goblins";
import {calcWinPointsForKill} from "../../../utils/gameState/winPoints/calcWinPointsForKill";
import {getPlayerById} from "../../../utils/players/getPlayerById";
import {COMBAT_TYPE} from "../../../constants/combat";
import {getHexDistance} from "../../../utils/coords/getHexDistance";
import {getLastUsedElementIndex} from "../../../utils/factions/elementals/getLastUsedElementIndex";
import {getElementForTokenType} from "../../../utils/factions/elementals/getElementForTokenType";
import {isStalePhaseAction} from "../../../utils/gameState/turn/phaseActionGuard";
import {
  getInitiativeActionRejection
} from "../../../utils/gameState/initiative/getInitiativeInterceptionRejection";
import {getRulesGameType} from "../../../utils/gameState/getRulesGameType";
import {applyBotPressureLatches} from "../../../utils/bots/pressure/applyBotPressureLatches";
import {
  checkIfAllPlayersCompletedPhase,
  checkIfParallelSupplyPhase,
  checkIfPlayerCompletedPhase,
  checkIfSupplyActorLocked,
  getPendingSupplyOwnerId,
  resolveSupplyActorId
} from "../../../utils/gameState/supply/parallelSupply";
import {findUndoableUnitMove} from "../../../utils/gameState/movement/findUndoableUnitMove";
import {getRoundLimit} from "../../../utils/checkIfFinalRound";
import {buildGameResults, getPlayerFinalWinPoints} from "../../../utils/gameEnd/buildGameResults";
import {findLastStandingPlayer} from "../../../utils/gameEnd/findLastStandingPlayer";
import {checkIfLastStandingEndRule} from "../../../utils/gameEnd/checkIfLastStandingEndRule";
import {checkIfCitylessEconomyRule} from "../../../utils/gameState/income/checkIfCitylessEconomyRule";
import {buildTrapStageCombatStats} from "../../../utils/gameState/buildCombatStats";

function getBaseTotalWinPoints(player) {
  return player.winPoints + Math.floor(player.coins / COINS_TO_WIN_POINTS_CONVERSION);
}

/**
 * Игрок, опережающий КАЖДОГО другого минимум на GAME_END_WIN_POINTS_GAP очков
 * по переданной формуле счета (или null, если такого нет).
 */
function findDominantPlayer(players, getTotalWinPoints) {
  return players.find((player) => {
    const playerScore = getTotalWinPoints(player);
    return players.every((other) => other.id === player.id
      || playerScore >= getTotalWinPoints(other) + GAME_END_WIN_POINTS_GAP);
  }) || null;
}

/**
 * Условие досрочного завершения матча — двойная проверка.
 *
 * Базовая формула (курс монет 2:1 у всех) недооценивает просвещенных: их
 * монеты в итоговых результатах идут по курсу 1:1, поэтому по базовому счету
 * лидер может определиться иначе, чем по фактическим итоговым очкам.
 * Поэтому после базовой проверки счет пересчитывается по фракционным курсам
 * и требуется, чтобы: лидер остался ТОТ ЖЕ и разрыв в 50+ очков сохранился.
 * В партиях без просвещенных обе формулы совпадают — поведение не меняется.
 */
function hasDominantPlayer(players) {
  const dominantByBaseRate = findDominantPlayer(players, getBaseTotalWinPoints);
  if (!dominantByBaseRate) {
    return false;
  }

  const dominantByFactionRate = findDominantPlayer(players, getPlayerFinalWinPoints);
  return dominantByFactionRate !== null && dominantByFactionRate.id === dominantByBaseRate.id;
}

const initialState = {
  gameType: null, // local или online
  // Правила, по которым проигрывается запись (только при gameType REPLAY):
  // запись локальной партии — например, прохождения обучения — идет по
  // локальным правилам, сетевого матча — null. См. getRulesGameType
  replayRulesGameType: null,
  // Планировщик ботов (волна 29): только локальная партия, только по явному
  // включению админом на HotSeatPlayersInitPage
  plannerBotsEnabled: false,
  isStarted: false,

  currentPlayerIndex: 0, // Идентификатор игрока, чей ход идет в текущий момент в текущей фазе
  nextInitiativePlayerId: null, // Идентификатор игрока, перехватившего инициативу в фазе строительства
  players: [], // Игроки будут добавляться динамически при старте игры
  turnOrder: [], // Порядок игроков в фазе размещения кварталов

  startingQuarters: {}, // Объект с координатами стартовых кварталов игроков
  // Пример: { 'blue': '1,2', 'dark_green': '7,3'}

  round: 0, // Текущий раунд
  finalRound: FINAL_ROUND, // Финальный раунд игры, после которого происходит подсчёт очков
  // Окно увеличенного дохода руин — от finalRound партии. false — только у
  // записей, сделанных до этого правила: их LOAD_STATE метки не несет, и окно
  // считается от FINAL_ROUND, как тогда (см. getRuinsIncomeRoundLimit)
  isRuinsIncomeByRoundLimit: true,
  // Правило «без городов — без монет»: без единого города нет доплат к доходу,
  // а в конце раунда монеты сгорают. false — только у записей, сделанных до
  // него (см. checkIfCitylessEconomyRule)
  isCitylessEconomyRule: true,
  // Бонус дохода ботов/лобби за каждый город (с 29.10). false — только у
  // записей, сделанных до него: их игроки с бонусом без зафиксированных чисел
  // получают прежние (см. checkIfPlayerBonusPerCityRule)
  isPlayerBonusPerCityRule: true,
  // Досрочный конец «в живых один» (см. findLastStandingPlayer). false — только
  // у записей, сделанных до него (см. checkIfLastStandingEndRule)
  isLastStandingEndRule: true,
  // Крепость на центральном квартале получает урон от стрельбы и при целых
  // базовых кварталах. false — только у записей, сделанных до правки (см.
  // checkIfCentralQuarterFortressDamageRule)
  isCentralQuarterFortressDamageRule: true,
  // Новое строение вне города не отнимает очков в конце хода строительства.
  // false — только у записей, сделанных до правки (см.
  // checkIfBuildingWinPointsInCitiesRule)
  isBuildingWinPointsInCitiesRule: true,
  // Партия сюжетной миссии: заканчивается по заданиям и финальному раунду,
  // а НЕ по отрыву в очках (см. hasDominantPlayer)
  isMissionMatch: false,

  turnEndTimestamp: null,
  startedAt: null,
  timerEnabled: true,
  fastMode: false,
  phase: 'field_preparation', // Текущая фаза: 'field_preparation','setup', 'supply', 'construction', 'war'
  warPhaseStage: null, // Текущий этап фазы войны: 'recruitment' | 'movement' | 'combat' - подэтап фазы войны
  phaseCompletedPlayers: [], // Игроки, завершившие текущую фазу
  playersConfirmedSetupPhase: [], // Игроки, подтвердившие завершение размещения стартовых кварталов
  // Одновременная фаза снабжения сетевой игры (см. utils/gameState/supply/parallelSupply):
  // флаг приходит в START_GAME только от сервера, поэтому записи матчей и
  // локальные игры сохраняют поочередное снабжение
  isParallelSupply: false,
  // Итог одновременной фазы снабжения уже применен (закрытие фазы): повторное
  // закрытие до START_NEXT_PHASE не должно морить отряды голодом второй раз
  isParallelSupplyCommitted: false,
  // Промо-матч против ИИ (приходит в START_GAME только от сервера, см.
  // startGame): признак матча, модерируемый чат и персональное время хода
  // {[playerId]: {durationMs, isActive}} (utils/phase/turnTimeLimits). Вне
  // ИИ-матча — false/false/{}: так их нет и в состоянии до START_GAME, и в
  // загрузке полного состояния, где этих полей нет вовсе (LOAD_STATE строится
  // поверх начального состояния)
  isAiMatch: false,
  chatEnabled: false,
  playerTurnTimeOverrides: {},

  // Кэш городов: { [playerColor]: [ { centralQuarter, quarter, farm, barracks, mine, ... }, ... ] }
  // Пример: { 'blue': [{
  //   centralQuarter: '1,2',
  //   quarter: ['1,3'],
  //   farm: ['1,4'],
  //   mine: ['1,1'],
  //   palaces: ['1,3'],
  // }], 'neutral': [...] }
  citiesCache: null,

  // Повреждения строений: { [hexKey]: damage }
  buildingsDamage: {},

  // Объект с координатами специальных строений: {[key]: [specialBuildingTokenId]}
  specialBuildings: {},

  // Массив списка городов, захваченных в предыдущий раз (не обязательно в последнем раунде):
  // [{city, fromPlayer, toPlayer, centralQuarterHexKey, cityWinPoints}]
  lastCapturedCities: [],

  // Массив строений, размещенных в текущей фазе строительства, но еще не проверенных
  pendingBuildings: [], // { hexKey, token, playerId, price, timestamp }
  invalidPendingBuildings: [], // Массив hexKey отложенных строений, которые не прошли валидацию

  // Массив hexKey всех fortresses, которые были построены в этом раунде
  // Такие крепости не влияют на результаты урона в ближнем бою.
  fortressesUnderConstruction: [],

  units: [], // Массив отрядов: { id, hexKey, ownerId, size, type, additionalSupply}

  pendingRecruitmentActions: [], // Массив действий найма: [{ unitId, actionType, quarterHexKey, price, sizeAdded }]
  pendingMovementActions: [], // Массив действий перемещения: [{ unitId, fromHexKey, toHexKey }]
  pendingShootingActions: [], // Массив действий стрельбы: [{ unitId, targetUnitId, fromHexKey, toHexKey, damage }]
  pendingMeleeDamage: [], // Массив маркеров урона ближнего боя: [{ unitId, hexKey, damage }]
  playersWaitedToConfirmCombatStage: [], // Массив игроков, которые должны подтвердить завершение этапа ближнего боя фазы войны (все участники ближнего боя)
  playersConfirmedCombatStage: [], // Массив игроков, подтвердили завершение этапа ближнего боя фазы войны
  meleePriorityTargets: {}, // {[unitId]: priorityTargetUnitId}
  pendingManeuverActions: [], // Массив действий маневра: [{ unitId, fromHexKey, toHexKey }]

  // === Supply system ===
  pendingSupply: [], // Массив действий снабжения: [{ farmHexKey, unitId, addedSize }]

  gameResults: null,
  extendedGameResults: null,
  // Почему партия завершилась досрочно (GAME_END_REASONS) — null при обычном конце
  gameEndReason: null,
  // Раунд, в котором партия закончилась. Нужен итогам матча: при досрочном
  // конце по отрыву в очках round перезаписывается на finalRound, и реально
  // сыгранный раунд иначе не восстановить. null — партия идет
  gameEndRound: null,
  // Рейтинговая ли партия — нужно итогам матча, в том числе при просмотре записи
  isRanked: false,
  // Подложка поля: идентификатор из configs/groundBackgrounds. Сервер выбирает
  // ее один раз при старте матча и присылает в START_GAME, локальные партии
  // выбирают сами. null — выбора не было, сцена подставит случайную
  groundBackground: null,

  // Массив результатов боя: [{ killedUnit, killerPlayer, winPoints }]
  combatResults: [],
  // Сводка того же этапа боя по игрокам (см. buildCombatStats) — пишется
  // вместе с combatResults; null — сводки нет (запись до нее)
  combatStats: null,

  // Переписка игроков в матче: [{ fromPlayerId, fromPlayerName, text, sentAt }].
  // Функция доступна только в локальной разработке (через нее договариваются
  // LLM-агенты, играющие по MCP) — сервер отбрасывает эти экшены вне dev.
  // Живет в общем состоянии, а не сбоку: так переписка попадает в запись
  // матча и воспроизводится в реплее вместе с ходами
  chatMessages: [],

  // Policy market
  policyMarket: [], // Список идентификаторов политик

  // FACTIONS
  leprechaunsStealCoinsActions: [],
  elementalsConstructionTokens: [],
  // Жетоны элементов Элементалей, вытянутые при планировании строительства в
  // чужой ход (см. construction/planning), — по игроку, пока его ход в фазе не
  // наступил. С началом хода игрока его жетоны становятся
  // elementalsConstructionTokens (COMPLETE_PHASE, sharedStateReducer). В общем
  // состоянии, чтобы бросок нельзя было переиграть перезагрузкой и чтобы он
  // дошел до записи матча
  elementalsPlanTokensByPlayerId: {},
  // История попыток соблазнения Суккубов — только для тостов/истории/сериализации.
  // Доступность действия проверяется общим флагом hasUsedAbilityThisWarPhase на
  // самом юните (сбрасывается за ход, общий для всех фракций со способностями
  // на этапе ABILITIES - см. configs/units.js), а не этим массивом
  succubesSeductionActions: [],

  // Ловушки Гоблинов, стоящие на поле прямо сейчас: [{ id, ownerId, hexKey }].
  // В отличие от succubesSeductionActions это не лог, а персистентный игровой
  // объект — существует до тех пор, пока не будет снят владельцем или не
  // сработает (см. openspec/changes/add-goblins-faction design.md Decision 2)
  goblinsTraps: [],
  // История попыток установки/снятия ловушки и повреждения строения Гоблинами
  // — только для тостов/истории/сериализации, не источник истины
  goblinsAbilityActions: [],
  // Убийства ловушкой, накопленные за текущий этап перемещения, но ещё не
  // перенесённые в combatResults — перенос происходит одним пакетом при
  // завершении этапа перемещения (APPLY_MOVEMENT_ACTIONS), чтобы модалка
  // результатов боя появлялась в конце этапа, а не посреди него (см.
  // design.md Decision 11)
  pendingGoblinsTrapKillResults: [],
  // Все срабатывания ловушек на ТЕКУЩЕМ этапе перемещения или маневра:
  // [{unitId, unit, died, finalHexKey, totalDamageTaken, triggeredTrapEvents}].
  // Живут ровно до конца этапа (чистятся там же, где
  // pendingGoblinsTrapKillResults) и КОПЯТСЯ: следующее срабатывание не должно
  // стирать с поля предыдущее — игрок должен видеть всю картину этапа
  // (погибшие отряды, плашки урона, контуры сработавших ловушек), а на ячейки
  // погибших нельзя встать до конца этапа (см.
  // calculateAvailableMovementCells). Общее состояние, а не приватное, чтобы
  // и картинка, и правило были одинаковыми у всех клиентов, у ботов и в
  // записях матчей
  goblinsTrapStageTriggers: [],

  // POLICIES
  isInitiativeInterceptedWithIsolationism: false,
};

// Начальное состояние партии: используется LOAD_STATE как чистая база при
// загрузке нового поля — состояние предыдущей партии не должно протекать
// в новую (дефект демо-игры меню: gameResults/phase/round прошлой партии
// глушили ботов и ломали фазу расстановки)
export const INITIAL_SHARED_GAME_STATE = initialState;

const sharedGameStateReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'SET_GAME_TYPE':
      return {
        ...state,
        gameType: action.payload.gameType,
        // Правила записи задаются только вместе с режимом просмотра — любая
        // другая смена режима их сбрасывает
        replayRulesGameType: action.payload.gameType === GAME_TYPES.REPLAY
          ? (action.payload.rulesGameType ?? null)
          : null,
      };

    // Планировщик ботов (волна 29) в локальной партии — админский тумблер
    // HotSeatPlayersInitPage. Демо меню принудительно сбрасывает в false
    case 'SET_BOTS_PLANNER_MODE':
      return {
        ...state,
        plannerBotsEnabled: Boolean(action.payload.isEnabled),
      };

    // Сюжетное появление стороны посреди миссии кампании («гномы
    // выкапываются»): добавляем игрока, если его ещё нет, — через
    // createPlayersState, НЕ трогая существующих, — и ставим его отряды.
    // turnOrder дополняется в конец: новичок ходит после всех
    // Партия идёт как сюжетная миссия (прохождение или запись в редакторе):
    // отключает досрочный конец матча по отрыву в очках
    case 'MISSION_START':
    case 'MISSION_EDITOR_START':
      return {...state, isMissionMatch: true};

    case 'MISSION_SPAWN': {
      const {player, units = []} = action.payload;

      let nextPlayers = state.players;
      let nextTurnOrder = state.turnOrder;
      if (player && !state.players.some((item) => item.color === player.color)) {
        const [created] = createPlayersState([player], getRulesGameType(state), {
          isPlayerBonusPerCityRule: checkIfPlayerBonusPerCityRule(state),
        });
        nextPlayers = [...state.players, created];
        nextTurnOrder = [...(state.turnOrder || []), created.id];
      }

      const occupied = new Set(state.units.map((unit) => unit.hexKey));
      const spawned = units
        .filter((unit) => !occupied.has(unit.hexKey))
        .map((unit) => {
          const unitConfig = getUnitTypeConfig(unit.type);
          const created = createUnit(
            unit.id, unit.hexKey, unit.ownerId, unit.type, null, unitConfig.maxSize
          );
          return unit.size ? {...created, size: unit.size} : created;
        });

      return {
        ...state,
        players: nextPlayers,
        turnOrder: nextTurnOrder,
        units: [...state.units, ...spawned],
      };
    }

    // Сюжетный уход стороны со сцены: отряды цвета снимаются с поля
    case 'MISSION_DESPAWN': {
      const leaving = state.players.find((item) => item.color === action.payload.color);
      if (!leaving) return state;
      return {
        ...state,
        units: state.units.filter((unit) => unit.ownerId !== leaving.id),
      };
    }

    // Редактор миссий: автор в любой момент передаёт игрока системе ботов
    // («дальше ходит ИИ»). Меняем ТОЛЬКО флаг — SET_PLAYERS здесь непригоден,
    // он пересоздал бы игроков и обнулил монеты/очки
    case 'SET_PLAYER_IS_BOT':
      return {
        ...state,
        players: state.players.map((player) => (player.color === action.payload.color
          ? {...player, isBot: Boolean(action.payload.isBot)}
          : player)),
      };

    // Перехват управления ботом админом (см. controlledBots). Один админ ведет
    // не больше одного бота: перехват второго снимает пометку с первого. Правка
    // точечная — SET_PLAYERS пересоздал бы игроков и обнулил монеты/очки
    case 'SET_BOT_CONTROLLER': {
      const {botPlayerId, controllerUserId} = action.payload;
      return {
        ...state,
        players: state.players.map((player) => {
          if (player.id === botPlayerId) {
            return {...player, botControlledByUserId: controllerUserId || null};
          }
          if (controllerUserId && player.botControlledByUserId === controllerUserId) {
            return {...player, botControlledByUserId: null};
          }
          return player;
        }),
      };
    }

    case 'SET_SPECIAL_BUILDING': {
      const {hexKey, buildingType} = action.payload;
      const pendingBuildings = state.pendingBuildings;
      const currentPlayer = state.players[state.currentPlayerIndex];
      if (!currentPlayer) return state;

      // Получаем цену строения
      const {priceCoins} = getBuildingPrice(buildingType, currentPlayer, pendingBuildings);

      // Проверяем, достаточно ли монет
      if (currentPlayer.coins < priceCoins) {
        console.warn('Cannot place special building: not enough coins');
        return state;
      }

      // Вычитаем монеты у игрока
      const updatedPlayers = state.players.map((player, index) => {
        if (index === state.currentPlayerIndex) {
          return {
            ...player,
            coins: Math.max(0, (player.coins || 0) - priceCoins),
          };
        }
        return player;
      });

      return {
        ...state,
        specialBuildings: {
          ...state.specialBuildings,
          [hexKey]: buildingType
        },
        pendingBuildings: [
          ...state.pendingBuildings,
          {
            hexKey,
            token: buildingType,
            playerId: currentPlayer.id,
            price: priceCoins,
            timestamp: Date.now()
          }
        ],
        players: updatedPlayers
      };
    }

    case 'PLACE_NEUTRAL_UNIT': {
      const {hexKey, unitType, unitId} = action.payload;

      // Проверяем, нет ли уже юнита на этой ячейке
      const existingUnit = state.units.find(u => u.hexKey === hexKey);
      if (existingUnit) {
        console.warn('Cannot place neutral unit: hex already occupied');
        return state;
      }

      const unitConfig = getUnitTypeConfig(unitType)
      // Создаем нейтральный юнит
      const newUnit = createUnit(unitId, hexKey, getNeutralPlayerMock().id, unitType, null, unitConfig.maxSize);

      return {
        ...state,
        units: [...state.units, newUnit]
      };
    }

    // Редактор миссий: отряд игрока по цвету. Владельца ищем среди players —
    // в фазе расстановки они уже загружены (см. HotSeatPlayersInitPage)
    case 'PLACE_MISSION_UNIT': {
      const {hexKey, unitType, unitId, ownerColor} = action.payload;

      if (state.units.find((unit) => unit.hexKey === hexKey)) {
        console.warn('Cannot place mission unit: hex already occupied');
        return state;
      }

      const owner = state.players.find((player) => player.color === ownerColor);
      if (!owner) {
        console.warn('Cannot place mission unit: unknown player color', ownerColor);
        return state;
      }

      const unitConfig = getUnitTypeConfig(unitType);
      const newUnit = createUnit(unitId, hexKey, owner.id, unitType, null, unitConfig.maxSize);

      return {
        ...state,
        units: [...state.units, newUnit],
      };
    }

    case 'REMOVE_NEUTRAL_UNIT': {
      const {hexKey} = action.payload;

      // Удаляем нейтральный юнит с указанной ячейки
      const updatedUnits = state.units.filter(u => !(u.hexKey === hexKey && u.ownerId === 'neutral'));

      return {
        ...state,
        units: updatedUnits
      };
    }

    // Инструмент "молоток" на этапе подготовки поля удаляет не только
    // токен ячейки (см. hexesReducer), но и все отряды на ней
    case 'CLEAR_HEX': {
      const hexKey = action.payload;
      const updatedUnits = state.units.filter(u => u.hexKey !== hexKey);
      if (updatedUnits.length === state.units.length) {
        return state;
      }

      return {
        ...state,
        units: updatedUnits
      };
    }

    case 'START_GAME': {
      // Note: hexes are needed to create players, this should be handled in rootReducer
      return state; // This will be handled by rootReducer which has access to hexes
    }

    case 'SET_STARTING_QUARTER': {
      const {playerId, hexKey} = action.payload;

      return {
        ...state,
        startingQuarters: {
          ...state.startingQuarters,
          [playerId]: hexKey,
        }
      };
    }

    case 'CONFIRM_SETUP_PHASE': {
      const playerId = action.payload.playerId;

      return {
        ...state,
        playersConfirmedSetupPhase: [...state.playersConfirmedSetupPhase, playerId],
      }
    }

    // Игрок закончил свою часть одновременной фазы снабжения. Текущий игрок не
    // двигается, итог снабжения не применяется: и то и другое — при закрытии
    // фазы (COMPLETE_PHASE), когда закончили все. Идемпотентно
    case 'COMPLETE_PLAYER_SUPPLY': {
      if (isStalePhaseAction(action, state)) return state;
      if (!checkIfParallelSupplyPhase(state)) return state;

      const playerId = action.payload?.playerId;
      if (!state.players.some((player) => player.id === playerId)
        || checkIfPlayerCompletedPhase(state, playerId)) {
        return state;
      }

      return {
        ...state,
        phaseCompletedPlayers: [...state.phaseCompletedPlayers, playerId],
      };
    }

    case 'COMPLETE_PHASE': {
      if (isStalePhaseAction(action, state)) return state;

      const gameType = getRulesGameType(state);
      const phase = state.phase;

      if (gameType !== GAME_TYPES.LOCAL && phase === PHASES.SETUP) {
        return state;
      }

      // Закрытие одновременной фазы снабжения: текущий игрок остается на месте
      // (следующую фазу откроет START_NEXT_PHASE), закончившими считаются все —
      // в том числе те, кого закрытие по таймеру застало посреди раздачи
      if (checkIfParallelSupplyPhase(state)) {
        // Закрывает фазу только сервер и только когда закончили все (по таймеру
        // он сперва заканчивает за оставшихся) — закрытие раньше применило бы
        // итог тем, кто еще раздает
        if (state.isParallelSupplyCommitted || !checkIfAllPlayersCompletedPhase(state)) {
          return state;
        }
        return {
          ...state,
          phaseCompletedPlayers: state.players.map((player) => player.id),
          isParallelSupplyCommitted: true,
        };
      }

      const completingPlayerId = state.players[state.currentPlayerIndex].id;
      const isAlreadyCompleted = state.phaseCompletedPlayers.includes(completingPlayerId);

      if (!isAlreadyCompleted) {
        const newPhaseCompletedPlayers = [...state.phaseCompletedPlayers, completingPlayerId];
        const allPlayersCompletedPhase = newPhaseCompletedPlayers.length === state.players.length;

        // Определяем следующего игрока
        let nextPlayerIdx = state.currentPlayerIndex;
        if (!allPlayersCompletedPhase) {
          // Используем порядок флагов из turnOrder для определения следующего игрока
          const turnOrder = state.turnOrder.length > 0
            ? state.turnOrder
            : state.players.map(p => p.id);

          // Находим индекс текущего игрока в порядке
          const currentIndexInOrder = turnOrder.indexOf(completingPlayerId);
          // Берем следующего игрока в порядке
          const nextIndexInOrder = (currentIndexInOrder + 1) % turnOrder.length;
          const nextPlayerId = turnOrder[nextIndexInOrder];

          // Находим индекс следующего игрока в массиве players
          nextPlayerIdx = state.players.findIndex(p => p.id === nextPlayerId);

          // Пропускаем игроков, которые уже завершили фазу
          let attempts = 0;
          while (newPhaseCompletedPlayers.includes(state.players[nextPlayerIdx].id) && attempts < state.players.length) {
            const nextNextIndexInOrder = (turnOrder.indexOf(state.players[nextPlayerIdx].id) + 1) % turnOrder.length;
            const nextNextPlayerId = turnOrder[nextNextIndexInOrder];
            nextPlayerIdx = state.players.findIndex(p => p.id === nextNextPlayerId);
            attempts++;
          }
        }

        // В фазе войны при передаче хода следующему игроку сбрасываем этап на recruitment
        // Каждый игрок должен пройти все этапы: RECRUITMENT -> MOVEMENT -> COMBAT
        let nextWarPhaseStage = state.warPhaseStage;
        let nextUnits = state.units;
        if (state.phase === PHASES.WAR && !allPlayersCompletedPhase) {
          // Сбрасываем флаги участия отрядов в стрельбе/ближнем бою/способности при смене игрока в фазе войны
          nextUnits = nextUnits.map(unit => ({
            ...unit,
            hasShotThisWarPhase: false,
            hasFoughtThisWarPhase: false,
            hasUsedAbilityThisWarPhase: false,
          }))

          if (!allPlayersCompletedPhase) {
            // Сбрасываем этап войны на первый (найм) для фазы войны следующего игрока
            nextWarPhaseStage = WAR_PHASE_STAGES.RECRUITMENT;
          }
        }

        return {
          ...state,
          phaseCompletedPlayers: newPhaseCompletedPlayers,
          currentPlayerIndex: nextPlayerIdx,
          warPhaseStage: nextWarPhaseStage,
          units: nextUnits,
        };
      }
      return state;
    }

    case 'START_NEXT_PHASE': {
      if (isStalePhaseAction(action, state)) return state;

      const {timestamp, newPolicy} = action.payload;

      const currentPhaseIndex = ROUND_PHASES.indexOf(state.phase);
      const nextPhase = ROUND_PHASES[(currentPhaseIndex + 1) % ROUND_PHASES.length];

      // Если мы прошли все фазы, увеличиваем раунд
      const isRoundEnding = nextPhase === ROUND_PHASES[0];

      // Определяем первого игрока для следующей фазы
      let nextPlayerIdx = 0;
      let updatedTurnOrder = [...state.turnOrder];

      // Если кто-то перехватил инициативу - меняем порядок игроков
      if (state.nextInitiativePlayerId !== null) {
        const initiativePlayerIndex = updatedTurnOrder.indexOf(state.nextInitiativePlayerId);
        if (initiativePlayerIndex !== -1) {
          // Перестраиваем порядок: игрок перехвативший инициативу становится первым, остальные идут по кругу
          updatedTurnOrder = [
            ...updatedTurnOrder.slice(initiativePlayerIndex),
            ...updatedTurnOrder.slice(0, initiativePlayerIndex)
          ];
        }
      }

      if (updatedTurnOrder.length > 0) {
        // Используем первого игрока из обновленного turnOrder
        const firstPlayerId = updatedTurnOrder[0];
        nextPlayerIdx = state.players.findIndex(p => p.id === firstPlayerId);
      }

      const citiesCache = state.citiesCache;
      const players = state.players;

      // Если раунд заканчивается, начисляем доход
      let nextPlayers = state.players;
      let nextUnits = state.units;
      let nextRound = state.round;
      let nextPolicyMarket = state.policyMarket;
      let nextBuildingsDamage = state.buildingsDamage;
      if (isRoundEnding) {
        nextRound += 1;

        if (nextRound !== 1) {
          // Конец раунда

          // Автовосстановление повреждений крепостей для Фортификационизма
          players.forEach((player) => {
            if (isPlayerPolicy(player, POLICIES.FORTIFICATIONISM)) {
              const playerCities = citiesCache[player.color] || [];

              // Находим город, на казарме которого находится выбранный отряд
              for (const city of playerCities) {
                const fortresses = city[SPECIAL_BUILDING_TOKENS.FORTRESS] || [];

                fortresses.forEach((fortressHexKey) => {
                  const fortressDamage = nextBuildingsDamage[fortressHexKey] || 0;

                  if (fortressDamage > 0) {
                    nextBuildingsDamage[fortressHexKey] = fortressDamage - POLICY_FORTIFICATIONISM_FORTRESS_ROUND_END_REPAIRMENT;
                  }
                });
              }
            }
          });

          // Начисляем доход всем игрокам и обновляем действующие политики.
          // У игрока, оставшегося к концу раунда (уже после захвата городов)
          // без единого города, монеты сгорают — и до подсчета отрыва в очках,
          // и до итогов партии. Старые записи — по-старому
          const isCitylessCoinsBurned = checkIfCitylessEconomyRule(state);
          nextPlayers = state.players.map(player => {
            const nextPolicy = player.nextPolicy ? player.nextPolicy : player.policy;
            const isCoinsBurned = isCitylessCoinsBurned && (citiesCache[player.color] || []).length === 0;

            return {
              ...player,
              coins: isCoinsBurned ? 0 : player.coins + player.coinsIncome,
              lastCoinsIncome: player.coinsIncome,
              winPoints: player.winPoints + player.winPointsIncome,
              lastWinPointsIncome: player.winPointsIncome,
              isClearedPolicyMarketThisTurn: false,
              isFanaticismConversionUsed: false,
              policy: nextPolicy,
              nextPolicy: null
            }
          });

          // Сбрасываем дополнительное снабжение отрядов, у которых оно осталось неизрасходованным
          nextUnits = nextUnits.map(unit => ({
            ...unit,
            additionalSupply: false
          }));

          // Сдвигаем на 1 рынок политик:
          // удаляется самая дешевая, приходит новая на место самой дорогой,
          // остальные сдвигаются по цене вниз.
          nextPolicyMarket = [...nextPolicyMarket.slice(1, nextPolicyMarket.length), newPolicy]
        }

        // Давление ботов на сильного живого игрока рейтинговой партии
        // взводится НА СТАРТЕ РАУНДА (см. src/configs/botPressure.js) — то
        // есть уже с учетом только что начисленного дохода очков. Считается
        // здесь, а не отдельным экшеном: этот же редьюсер применяют и сервер,
        // и клиент-делегат бота, и клиенты живых игроков, поэтому признаки
        // получаются одинаковыми у всех без лишней сетевой синхронизации
        nextPlayers = applyBotPressureLatches(nextPlayers);
      }

      const nextWarPhaseStage = nextPhase === PHASES.WAR ? WAR_PHASE_STAGES.RECRUITMENT : null;

      // Проверяем, закончилась ли игра. Досрочный конец по отрыву в очках —
      // правило обычного матча: в сюжетной миссии он оборвал бы партию
      // посреди сценария (захват вражеского города легко даёт нужный разрыв),
      // а миссия заканчивается заданиями и финальным раундом
      let gameResults = null;
      let gameEndReason = state.gameEndReason;
      let gameEndRound = state.gameEndRound;
      const isEarlyEndAllowed = isRoundEnding && !state.isMissionMatch && hasDominantPlayer(nextPlayers);
      // Тем же правилом обычного матча — «в живых один»: остальные игроки
      // остались без отрядов и городов, а уцелевший опережает каждого по
      // итоговым очкам (отряды — после конца раунда, города — после захватов)
      const isLastStandingEnd = isRoundEnding && state.round > 0 && !state.isMissionMatch
        && checkIfLastStandingEndRule(state)
        && findLastStandingPlayer(nextPlayers, nextUnits, citiesCache) !== null;
      // Лимит раундов партии. Миссия с finalRound: null раундов не
      // ограничивает — она заканчивается только заданиями (раньше сравнение
      // с null обрывало ее после первого же раунда)
      const roundLimit = getRoundLimit(state.finalRound);
      const isRoundLimitExceeded = roundLimit !== null && nextRound > roundLimit;
      if (isRoundLimitExceeded || isEarlyEndAllowed || isLastStandingEnd) {
        // Игра окончена - рассчитываем итоговые очки для каждого игрока
        gameResults = buildGameResults(nextPlayers, citiesCache);
        // Партию раньше последнего раунда закончили отрыв в очках или
        // выбывшие соперники — итоги матча об этом скажут (сыгран последний
        // раунд — обычный конец). Сошлись оба — причина прежняя, отрыв
        if (!isRoundLimitExceeded) {
          gameEndReason = isEarlyEndAllowed
            ? GAME_END_REASONS.WIN_POINTS_GAP
            : GAME_END_REASONS.LAST_PLAYER_STANDING;
        }
        // Доигран раунд state.round: доход его конца уже начислен, а отрыв
        // проверен после дохода. Запоминаем ДО перезаписи nextRound ниже —
        // после нее номер потерян
        gameEndRound = state.round;
        nextRound = roundLimit ?? state.round;
      } else if (nextPhase === PHASES.CONSTRUCTION) {
        // Действия фазы строительства — на ее начало: рабочие дворцы, политика,
        // фракция и бонус (тот же расчет расшифровывает панель статов в шапке)
        nextPlayers = nextPlayers.map(player => ({
          ...player,
          constructionPhaseActions: sumConstructionActionsSources(getConstructionActionsSources(player, {
            round: nextRound,
            gameType: getRulesGameType(state),
            workingPalacesCount: countWorkingPalaces(
              player, state.citiesCache, state.units, state.specialBuildings, state.buildingsDamage
            ),
          })),
        }));
      }

      // При новом раунде убираем из fortressesUnderConstruction те крепости,
      // достроить которые никто не мешает: отряд противника держит стройку,
      // стоя смежно с крепостью ИЛИ на самой ее ячейке (та же проверка
      // показывает «Строительство заблокировано» при инспекции ячейки)
      let nextFortressesUnderConstruction = state.fortressesUnderConstruction;
      if (isRoundEnding) {
        nextFortressesUnderConstruction = state.fortressesUnderConstruction.filter(fortressHexKey => {
          const owner = findOwner(fortressHexKey, state.players, state.citiesCache);
          // Владельца города нет (город распался) — достраивать некому
          if (!owner) return true;
          return checkIfFortressConstructionBlocked(fortressHexKey, owner.id, state.units);
        });
      }

      // При входе в фазу войны сбрасываем флаги участия отрядов
      if (nextPhase === PHASES.WAR) {
        nextUnits = nextUnits.map(unit => ({
          ...unit,
          hasShotThisWarPhase: false,
          hasFoughtThisWarPhase: false,
          hasUsedAbilityThisWarPhase: false,
        }));
      }

      return {
        ...state,
        policyMarket: nextPolicyMarket,
        ...(timestamp !== undefined && {turnEndTimestamp: timestamp}),
        phase: nextPhase,
        warPhaseStage: nextWarPhaseStage,
        round: nextRound,
        currentPlayerIndex: nextPlayerIdx,
        phaseCompletedPlayers: [],
        isParallelSupplyCommitted: false,
        turnOrder: updatedTurnOrder,
        nextInitiativePlayerId: null, // Сбрасываем перехват инициативы после перехода фазы
        // Жетоны планирования — только в пределах фазы строительства
        elementalsPlanTokensByPlayerId: {},
        players: nextPlayers,
        units: nextUnits,
        fortressesUnderConstruction: nextFortressesUnderConstruction,
        gameResults: gameResults,
        gameEndReason,
        gameEndRound,
      };
    }

    case 'RECRUIT_UNIT': {
      const {hexKey, unitType, factionId, price, unitId, isRatmenForSupplyRecruitment} = action.payload;

      // Нанимает игрок из payload (найм крысолюдов за снабжение в одновременной
      // фазе), без него — текущий игрок
      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }
      const currentPlayer = state.players.find((player) => player.id === actorId);
      if (!currentPlayer) {
        return state;
      }

      // Проверяем, что на казарме нет другого отряда
      const existingUnit = state.units.find(u => u.hexKey === hexKey);
      if (existingUnit) {
        console.warn('Cannot recruit unit: barracks already occupied');
        return state;
      }

      // Проверяем достаточно ли монет
      if (currentPlayer.coins < price) {
        console.warn('Cannot recruit unit: not enough coins');
        return state;
      }

      // Создаем новый отряд с использованием функции createUnit
      const newUnit = createUnit(unitId, hexKey, currentPlayer.id, unitType, factionId, 1);

      if (isRatmenForSupplyRecruitment) {
        newUnit.size = 0;
        // Отряд, нанятый за снабжение, появится на поле по итогу фазы снабжения
        // (при ее применении метка снимается): до тех пор он не блокирует чужие
        // фермы и пути снабжения (см. checkIfHexBlockedByEnemyUnit). В
        // одновременной фазе иначе итог соседей зависел бы от того, кто успел
        // нанять раньше их раздачи
        newUnit.isSupplyRecruit = true;
      }

      if (isPlayerPolicy(currentPlayer, POLICIES.TOTALITARIANISM)) {
        switch (newUnit.type) {
          case UNIT_TYPES.D10.id:
            newUnit.size = POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D10;
            break;
          case UNIT_TYPES.D20.id:
            newUnit.size = POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D20;
            break;
          case UNIT_TYPES.D8.id:
            newUnit.size = POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D8;
            break;
          case UNIT_TYPES.D12.id:
            newUnit.size = POLICY_TOTALITARIANISM_UNIT_RECRUITMENT_SIZE_D12;
            break;
          default:
        }
      }

      return {
        ...state,
        units: [...state.units, newUnit],
      };
    }

    case 'ADD_PENDING_MOVEMENT_ACTION': {
      const {unitId, fromHexKey, toHexKey, additionalSupply} = action.payload;

      // Сначала обновляем позицию отряда
      const updatedUnits = state.units.map(u => {
        if (u.id === unitId) {
          return {...u, hexKey: toHexKey};
        }
        return u;
      });

      return {
        ...state,
        units: updatedUnits,
        pendingMovementActions: [...state.pendingMovementActions, {
          unitId,
          fromHexKey,
          toHexKey,
          additionalSupply
        }]
      };
    }

    case 'ADD_PENDING_MANEUVER_ACTION': {
      const {unitId, fromHexKey, toHexKey} = action.payload;

      return {
        ...state,
        pendingManeuverActions: [...state.pendingManeuverActions, {
          unitId,
          fromHexKey,
          toHexKey
        }]
      };
    }

    case 'UNDO_PENDING_MANEUVER_ACTION': {
      const maneuverToUndo = findUndoableUnitMove(
        state.pendingManeuverActions, state.units, action.payload?.unitId
      );

      if (!maneuverToUndo) return state;

      const {unitId, fromHexKey} = maneuverToUndo;

      // Возвращаем отряд на исходную позицию
      const updatedUnits = state.units.map(u => {
        if (u.id === unitId) {
          return {
            ...u,
            hexKey: fromHexKey,
          };
        }
        return u;
      });

      return {
        ...state,
        units: updatedUnits,
        pendingManeuverActions: state.pendingManeuverActions.filter(maneuver => maneuver !== maneuverToUndo)
      };
    }

    case 'UNDO_PENDING_MOVEMENT_ACTION': {
      const movementToUndo = findUndoableUnitMove(
        state.pendingMovementActions, state.units, action.payload?.unitId
      );

      if (!movementToUndo) return state;

      const {unitId, fromHexKey} = movementToUndo;

      // Move the unit back to its original position
      const updatedUnits = state.units.map(u => {
        if (u.id === unitId) {
          return {
            ...u,
            hexKey: fromHexKey
          };
        }
        return u;
      });

      return {
        ...state,
        units: updatedUnits,
        pendingMovementActions: state.pendingMovementActions.filter(movement => movement !== movementToUndo)
      };
    }

    case 'ADD_PENDING_SHOOTING_ACTION': {
      const {
        id,
        shooterUnitId,
        targetUnitId,
        targetHexKey,
        diceRoll1,
        diceRoll2,
        isCentralQuarterDamageIgnored,
        damage,
        bonus,
        isUnitDamageOnly,
      } = action.payload;

      const nextUnits = state.units.map(unit => {
        if (unit.id === shooterUnitId) {
          return {
            ...unit,
            hasShotThisWarPhase: true,
          };
        }
        return unit;
      });

      return {
        ...state,
        units: nextUnits,
        pendingShootingActions: [
          ...(state.pendingShootingActions || []),
          {
            id,
            shooterUnitId,
            targetUnitId,
            targetHexKey,
            diceRoll1,
            diceRoll2,
            bonus,
            damage,
            isCentralQuarterDamageIgnored,
            isUnitDamageOnly,
          }
        ]
      };
    }

    case 'REROLL_PENDING_SHOOTING_ACTION': {
      const {id, diceRoll1, diceRoll2, newDamage, newBonus} = action.payload;
      const pendingShootingActions = state.pendingShootingActions;

      const actionToReroll = pendingShootingActions.find(action => action.id === id);

      const nextUnits = state.units.map(unit => {
        return unit.id === actionToReroll.shooterUnitId ? {
          ...unit,
          additionalSupply: false
        } : unit;
      });

      const nextPendingShootingActions = pendingShootingActions.map(action => {
        return action.id === id ? {
          ...actionToReroll,
          hasRerolled: true,
          diceRoll1,
          diceRoll2,
          bonus: newBonus,
          damage: newDamage,
        } : action;
      });

      return {
        ...state,
        units: nextUnits,
        pendingShootingActions: nextPendingShootingActions,
      }
    }

    case 'TOGGLE_INITIATIVE_INTERCEPTION': {
      const currentPlayer = state.players[state.currentPlayerIndex];

      // Если инициативу уже перехватил другой игрок - ничего не меняем
      if (state.nextInitiativePlayerId !== null && state.nextInitiativePlayerId !== currentPlayer.id) {
        return state;
      }

      // Остальные правила перехвата до сих пор жили только в кнопке и у ботов,
      // поэтому клиент, шлющий экшен мимо кнопки (MCP-агент), перехватывал
      // инициативу, которой уже владел, и занимал слот перехвата раунда —
      // сопернику перехват становился недоступен. Держим правило в движке:
      // редьюсер общий у клиента, сервера, ботов и локальных партий, и обойти
      // его нельзя даже сырым экшеном.
      //
      // ОТМЕНА СВОЕГО ПЕРЕХВАТА разрешена всегда: она возвращает потраченное
      // действие, и проверка остатка действий заперла бы игрока в перехвате.
      //
      // ЗАПИСЬ МАТЧА проигрывается по правилам своей партии (как isParallelSupply):
      // старые записи содержат перехваты, сделанные до этой проверки, и
      // воспроизводиться должны как есть, иначе порядок хода в записи разойдется
      // с тем, что было на самом деле
      const interceptionRejection = getInitiativeActionRejection(state, action);
      if (interceptionRejection && state.gameType !== GAME_TYPES.REPLAY) {
        console.warn('[TOGGLE_INITIATIVE_INTERCEPTION] rejected:', interceptionRejection, currentPlayer.id);
        return state;
      }

      // Переключаем состояние: если уже перехватили - отменяем, иначе устанавливаем
      return {
        ...state,
        nextInitiativePlayerId: state.nextInitiativePlayerId === currentPlayer.id
          ? null
          : currentPlayer.id,
        isInitiativeInterceptedWithIsolationism: false,
      };
    }

    case 'ADD_PENDING_RECRUITMENT_ACTION': {
      const {unitId, actionType, quarterHexKey, price, sizeAdded} = action.payload;

      const currentPlayer = state.players[state.currentPlayerIndex];

      if (currentPlayer.coins < price) {
        console.warn('Cannot add recruitment action: not enough coins');
        return state;
      }

      const nextPlayers = state.players.map((p) => {
        if (p.id === currentPlayer.id) {
          return {
            ...p,
            coins: p.coins - price
          };
        }
        return p;
      });

      const nextPendingRecruitmentActions = [...state.pendingRecruitmentActions, {
        unitId,
        actionType,
        quarterHexKey,
        price,
        sizeAdded
      }];

      return {
        ...state,
        pendingRecruitmentActions: nextPendingRecruitmentActions,
        players: nextPlayers,
      };
    }

    case 'UNIT_UPGRADE': {
      const {unitId, prevType, newType, price} = action.payload;

      const currentPlayer = state.players[state.currentPlayerIndex];

      if (currentPlayer.coins < price) {
        console.warn('Cannot upgrade unit: not enough coins');
        return state;
      }

      // Лимит отрядов одного типа держится и при улучшении: вариант может
      // вести в «младший» тип (d10 -> d8), которого у игрока уже по лимиту.
      // Проверка здесь, а не только в модалке, — экшен приходит и по сети
      if (checkIfUnitTypeChangeExceedsLimit(unitId, newType, currentPlayer, state.units)) {
        console.warn('Cannot upgrade unit: same type unit limit exceeded');
        return state;
      }

      // Update unit type and maxSize
      const updatedUnits = state.units.map(unit => {
        if (unit.id === unitId) {
          // Лимит размера берется с учетом фракции отряда (у великанов он
          // общий для всех типов) — иначе апгрейд сбрасывал бы фракционный
          // лимит на лимит типа и резал текущий размер отряда по нему
          const newMaxSize = getUnitMaxSizeForFaction(newType, unit.factionId);
          return {
            ...unit,
            type: newType,
            maxSize: newMaxSize,
            // Keep current size, but cap it at new maxSize if it exceeds
            size: Math.min(unit.size, newMaxSize)
          };
        }
        return unit;
      });

      // Subtract coins from player
      const updatedPlayers = state.players.map(player => {
        if (player.id === currentPlayer.id) {
          return {
            ...player,
            coins: player.coins - price
          };
        }
        return player;
      });

      // Add to pending recruitment actions. prevSize — размер до апгрейда:
      // апгрейд режет размер по новому лимиту, и отмена должна вернуть срезанное
      const upgradedUnit = state.units.find(unit => unit.id === unitId);
      const updatedPendingRecruitmentActions = [...state.pendingRecruitmentActions, {
        unitId,
        actionType: 'UNIT_UPGRADE',
        prevType,
        newType,
        price,
        prevSize: upgradedUnit?.size,
      }];

      return {
        ...state,
        units: updatedUnits,
        players: updatedPlayers,
        pendingRecruitmentActions: updatedPendingRecruitmentActions
      };
    }

    case 'SHOW_UNIT_UPGRADE_MODAL': {
      return {
        ...state,
        showUnitUpgradeModal: true,
        unitForUpgradeId: action.payload.unitId
      };
    }

    case 'HIDE_UNIT_UPGRADE_MODAL': {
      return {
        ...state,
        showUnitUpgradeModal: false,
        unitForUpgradeId: null
      };
    }

    case 'REPAIR_BUILDING': {
      const {hexKey, damage, tokenLabel} = action.payload;
      const currentDamage = state.buildingsDamage[hexKey];
      if (!currentDamage || currentDamage <= 0) {
        return state;
      }
      const nextBuildingsDamage = {...state.buildingsDamage};
      if (currentDamage <= damage) {
        delete nextBuildingsDamage[hexKey];
      } else {
        nextBuildingsDamage[hexKey] = currentDamage - damage;
      }
      return {
        ...state,
        buildingsDamage: nextBuildingsDamage,
        pendingBuildings: [
          ...state.pendingBuildings,
          {
            type: CONSTRUCTION_ACTION_TYPES.BUILDING_REPAIRMENT,
            hexKey,
            damage,
            tokenLabel,
            playerId: state.players[state.currentPlayerIndex].id,
            timestamp: Date.now()
          }
        ]
      };
    }

    case 'UNDO_REPAIR_BUILDING': {
      const {hexKey, damage} = action.payload;
      const nextBuildingsDamage = {...state.buildingsDamage};
      nextBuildingsDamage[hexKey] = (nextBuildingsDamage[hexKey] || 0) + damage;

      // Удаляем последнее действие ремонта для данного hexKey
      const reversedPending = [...state.pendingBuildings].reverse();
      const repairIndex = reversedPending.findIndex(
        b => b.type === CONSTRUCTION_ACTION_TYPES.BUILDING_REPAIRMENT && b.hexKey === hexKey
      );
      let nextPendingBuildings = state.pendingBuildings;
      if (repairIndex !== -1) {
        const originalIndex = state.pendingBuildings.length - 1 - repairIndex;
        nextPendingBuildings = [
          ...state.pendingBuildings.slice(0, originalIndex),
          ...state.pendingBuildings.slice(originalIndex + 1)
        ];
      }

      return {
        ...state,
        buildingsDamage: nextBuildingsDamage,
        pendingBuildings: nextPendingBuildings
      };
    }

    case 'UNDO_PENDING_RECRUITMENT_ACTION': {
      const lastRecruitmentAction = action.payload.lastRecruitmentAction;
      const {unitId, price, actionType, prevType} = lastRecruitmentAction;

      const currentPlayer = state.players[state.currentPlayerIndex];
      if (!currentPlayer) return state;

      // Снимается ровно переданное действие: у кнопки отмены это последнее
      // действие хода, у двойного клика по отряду — последнее действие ЭТОГО
      // отряда. Сравнение по полям: экшен приходит и по сети, с копией записи.
      // Действия одного отряда снимаются только с конца — найм отряда раньше
      // его докупки размера или улучшения оставил бы их на исчезнувшем отряде
      const recruitmentActionFields = ['unitId', 'actionType', 'quarterHexKey', 'price', 'sizeAdded', 'prevType', 'newType'];
      const undoneIndex = state.pendingRecruitmentActions.findLastIndex(
        (pending) => recruitmentActionFields.every((field) => pending[field] === lastRecruitmentAction[field])
      );
      if (undoneIndex === -1
        || state.pendingRecruitmentActions.slice(undoneIndex + 1).some((pending) => pending.unitId === unitId)) {
        return state;
      }
      // Вернувшись к прежнему типу, отряд не должен вывести игрока за лимит
      // отрядов этого типа (после улучшения мог быть нанят другой такой отряд)
      if (checkIfUnitUpgradeUndoExceedsLimit(lastRecruitmentAction, currentPlayer, state.units)) {
        return state;
      }

      let updatedUnits = state.units;

      if (actionType === 'UNIT_RECRUITMENT') {
        // For unit recruitment: remove the unit completely
        updatedUnits = state.units.filter(u => u.id !== unitId);
      } else if (actionType === 'UNIT_UPGRADE') {
        // For unit upgrade: revert unit type back to previous
        updatedUnits = state.units.map(unit => {
          if (unit.id === unitId) {
            return {
              ...unit,
              type: prevType,
              // Как и при самом апгрейде — лимит с учетом фракции отряда
              maxSize: getUnitMaxSizeForFaction(prevType, unit.factionId),
              // Апгрейд срезал размер по новому лимиту — возвращаем срезанное.
              // У записей, сделанных до появления prevSize, размер не трогаем
              ...(lastRecruitmentAction.prevSize !== undefined ? {size: lastRecruitmentAction.prevSize} : {}),
            };
          }
          return unit;
        });
      } else if (actionType === 'SIZE_RECRUITMENT') {
        // For size recruitment: since size is not changed immediately (only applied when stage completes),
        // we don't need to decrease the unit's size here - just refund coins
        // The size will remain unchanged since it was never updated
      }

      // Refund player's coins
      const updatedPlayers = state.players.map((player) => {
        if (player.id === currentPlayer.id) {
          return {
            ...player,
            coins: player.coins + price
          };
        }
        return player;
      });

      const updatedPendingRecruitmentActions = state.pendingRecruitmentActions
        .filter((pending, index) => index !== undoneIndex);

      return {
        ...state,
        units: updatedUnits,
        players: updatedPlayers,
        pendingRecruitmentActions: updatedPendingRecruitmentActions
      };
    }

    case 'MOVE_UNIT': {
      const {unitId, newHexKey} = action.payload;
      return {
        ...state,
        units: state.units.map(unit =>
          unit.id === unitId
            ? {...unit, hexKey: newHexKey}
            : unit
        )
      };
    }

    case 'UNDO_PENDING_SUPPLY_ACTION': {
      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }
      const currentPlayer = state.players.find((player) => player.id === actorId);

      // С playerId отменяется последнее снабжение ЭТОГО игрока: в одновременной
      // фазе последним в общем списке часто оказывается чужое. Записи без
      // playerId в экшене (старые записи матчей) — прежняя отмена последнего
      const hasExplicitActor = Boolean(action.payload?.playerId);
      const actorSupply = hasExplicitActor
        ? state.pendingSupply.filter((supply) => getPendingSupplyOwnerId(supply, state) === actorId)
        : state.pendingSupply;

      // Get the last pending supply action
      let nextPendingSupply = [...actorSupply].sort((a, b) => {
        if (a.isNotUndoable && !b.isNotUndoable) {
          return -1;
        } else if (b.isNotUndoable && !a.isNotUndoable) {
          return 1;
        } else {
          return 0;
        }
      });

      const lastSupply = nextPendingSupply[nextPendingSupply.length - 1];

      if (!lastSupply) {
        console.error('[UNDO_PENDING_SUPPLY_ACTION] player has no lastSupply:', currentPlayer?.id);
        return state;
      }
      if (lastSupply.isNotUndoable) {
        console.error('[UNDO_PENDING_SUPPLY_ACTION] player lastSupply isNotUndoable', currentPlayer?.id);
        return state;
      }

      let updatedUnits = state.units;

      if (lastSupply.isRatmenRecruitment) {
        updatedUnits = updatedUnits.filter(unit => unit.hexKey !== lastSupply.farmHexKey);
      }

      if (hasExplicitActor) {
        // Порядок чужих записей не трогаем — снимается ровно одна запись игрока
        return {
          ...state,
          units: updatedUnits,
          pendingSupply: state.pendingSupply.filter((supply) => supply !== lastSupply),
        };
      }

      nextPendingSupply = nextPendingSupply.slice(0, -1);

      return {
        ...state,
        units: updatedUnits,
        pendingSupply: nextPendingSupply,
      };
    }

    case 'UNDO_UNIT_PENDING_SUPPLY': {
      const {unitId} = action.payload;

      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }
      // Чужой отряд отменять нельзя (в одновременной фазе снабжают все сразу)
      if (action.payload?.playerId) {
        const unit = state.units.find((candidate) => candidate.id === unitId);
        if (unit && unit.ownerId !== actorId) {
          return state;
        }
      }

      // Точечная отмена снабжения отряда (двойной клик по отряду в фазе
      // снабжения): сначала снимается дополнительное снабжение, следующим
      // вызовом — базовое. Порядок обязателен: осиротевшее «дополнительное»
      // без базового при применении снабжения читалось бы как базовое
      const unitSupplies = state.pendingSupply.filter(supply => supply.unitId === unitId);
      const additionalSupply = unitSupplies.find(supply => supply.additionalSupply);
      const supplyToUndo = additionalSupply
        || unitSupplies.find(supply => !supply.additionalSupply);

      if (!supplyToUndo || supplyToUndo.isNotUndoable) {
        return state;
      }

      let updatedUnits = state.units;
      // Базовое снабжение нанятого за снабжение d6 крысолюдов — это и есть
      // найм: его отмена убирает сам отряд (как и в UNDO_PENDING_SUPPLY_ACTION)
      if (!supplyToUndo.additionalSupply && supplyToUndo.isRatmenRecruitment) {
        updatedUnits = updatedUnits.filter(unit => unit.id !== unitId);
      }

      return {
        ...state,
        units: updatedUnits,
        pendingSupply: state.pendingSupply.filter(supply => supply !== supplyToUndo),
      };
    }

    case 'UNDO_FARM_PENDING_SUPPLY': {
      const {farmHexKey} = action.payload;

      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }
      const hasExplicitActor = Boolean(action.payload?.playerId);

      // Точечная отмена снабжения, отданного С ФЕРМЫ (двойной клик по ферме):
      // снимается ПОСЛЕДНЕЕ отданное с нее снабжение, и его единица
      // возвращается ферме (остаток фермы считается из pendingSupply, см.
      // calcFarmRemainingSupply). Батч доп. снабжения великанов ссылается на
      // несколько ферм сразу (farmHexKeys) — он тоже считается отданным с
      // каждой из них
      const isFromFarm = (supply) => supply.farmHexKey === farmHexKey
        || Boolean(supply.farmHexKeys?.includes(farmHexKey));

      let supplyToUndo = null;
      for (let i = state.pendingSupply.length - 1; i >= 0; i--) {
        const supply = state.pendingSupply[i];
        if (isFromFarm(supply) && !supply.isNotUndoable
          && (!hasExplicitActor || getPendingSupplyOwnerId(supply, state) === actorId)) {
          supplyToUndo = supply;
          break;
        }
      }

      if (!supplyToUndo) {
        return state;
      }

      // Снимая БАЗОВОЕ снабжение отряда, снимаем и его дополнительное:
      // осиротевшее «дополнительное» без базового при применении снабжения
      // читалось бы как базовое (та же инвариантность, что в
      // UNDO_UNIT_PENDING_SUPPLY). Единицы этих снятых записей вернутся своим
      // фермам — в том числе чужим для кликнутой
      const removedSupplies = new Set([supplyToUndo]);
      if (!supplyToUndo.additionalSupply) {
        state.pendingSupply.forEach((supply) => {
          if (supply.unitId === supplyToUndo.unitId && supply.additionalSupply) {
            removedSupplies.add(supply);
          }
        });
      }

      let updatedUnits = state.units;
      // Базовое снабжение нанятого за снабжение d6 крысолюдов — это и есть
      // найм: его отмена убирает сам отряд (как и в UNDO_UNIT_PENDING_SUPPLY)
      if (!supplyToUndo.additionalSupply && supplyToUndo.isRatmenRecruitment) {
        updatedUnits = updatedUnits.filter(unit => unit.id !== supplyToUndo.unitId);
      }

      return {
        ...state,
        units: updatedUnits,
        pendingSupply: state.pendingSupply.filter(supply => !removedSupplies.has(supply)),
      };
    }

    // Сброс всей раздачи снабжения разом («Распределить заново» в футере фазы
    // снабжения) — та же семантика, что у поштучной отмены: неотменяемое
    // снабжение (авто-раздача инсектоидов) остается, а отряды, нанятые
    // крысолюдами ЗА снабжение, исчезают вместе со своим наймом
    case 'RESET_PENDING_SUPPLY': {
      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }
      // С playerId сбрасывается раздача только этого игрока, без него (старые
      // записи) — вся, как раньше: тогда раздавал один ходящий игрок
      const hasExplicitActor = Boolean(action.payload?.playerId);
      const supplyToUndo = state.pendingSupply.filter(supply => !supply.isNotUndoable
        && (!hasExplicitActor || getPendingSupplyOwnerId(supply, state) === actorId));
      if (supplyToUndo.length === 0) {
        return state;
      }

      const recruitedUnitIds = new Set(
        supplyToUndo
          .filter(supply => supply.isRatmenRecruitment && !supply.additionalSupply)
          .map(supply => supply.unitId)
      );

      return {
        ...state,
        units: recruitedUnitIds.size > 0
          ? state.units.filter(unit => !recruitedUnitIds.has(unit.id))
          : state.units,
        pendingSupply: state.pendingSupply.filter(supply => !supplyToUndo.includes(supply)),
      };
    }

    case 'CONFIRM_COMBAT_STAGE': {
      const playerId = action.payload.playerId;

      return {
        ...state,
        playersConfirmedCombatStage: [...state.playersConfirmedCombatStage, playerId],
      }
    }

    case 'APPLY_MANEUVER_ACTIONS': {
      const pendingManeuverActions = state.pendingManeuverActions;
      // Убийство ловушкой Гоблинов на этапе манёвра не оставляет запись в
      // pendingManeuverActions (юнит уже удалён из units - см.
      // APPLY_GOBLINS_TRAP_MOVEMENT, ветка unitDied), поэтому старый ранний
      // выход только по pendingManeuverActions.length молча пропускал перенос
      // накопленных убийств в combatResults - модалка результатов боя для
      // такой гибели никогда не открывалась (найдено пользователем в ручном
      // тестировании; тот же перенос уже сделан для перемещения в
      // APPLY_MOVEMENT_ACTIONS, см. design.md)
      const hasPendingTrapKillResults = state.pendingGoblinsTrapKillResults.length > 0;
      if (pendingManeuverActions.length === 0 && !hasPendingTrapKillResults) {
        return state;
      }

      const currentPlayer = state.players[state.currentPlayerIndex];

      const nextUnits = pendingManeuverActions.length > 0
        ? state.units.map(unit => {
          if (unit.ownerId !== currentPlayer.id) {
            return unit;
          }

          const hasManeuvered = pendingManeuverActions.some(action => {
            return action.unitId === unit.id;
          })

          return {
            ...unit,
            additionalSupply: unit.additionalSupply && !hasManeuvered,
          }
        })
        : state.units;

      // ЗАМЕНЯЕТ combatResults, а не дописывает в конец - тот же принцип, что
      // и у APPLY_SHOOTING_ACTIONS/APPLY_MELEE_DAMAGE (оба строят массив
      // combatResults для СВОЕГО этапа с нуля и присваивают его напрямую, не
      // спредя предыдущий). combatResults чистится только локально
      // (clearCombatResults - noServerSync), поэтому клиент, не закрывший
      // модалку результатов ПРЕДЫДУЩЕГО этапа, увидел бы в ней смесь старых и
      // новых записей при дописывании (найдено внешним ревью ветки)
      const nextCombatResults = hasPendingTrapKillResults
        ? state.pendingGoblinsTrapKillResults
        : state.combatResults;
      const nextCombatStats = hasPendingTrapKillResults
        ? buildTrapStageCombatStats(state.pendingGoblinsTrapKillResults, state.goblinsTrapStageTriggers, nextUnits, state.players)
        : state.combatStats;

      return {
        ...state,
        units: nextUnits,
        pendingManeuverActions: [],
        combatResults: nextCombatResults,
        combatStats: nextCombatStats,
        pendingGoblinsTrapKillResults: [],
        goblinsTrapStageTriggers: [],
      }
    }

    // === NEW: War phase stage ===
    case 'SET_WAR_PHASE_STAGE': {
      if (isStalePhaseAction(action, state)) return state;

      const {stage, timestamp} = action.payload;

      return {
        ...state,
        ...(timestamp !== undefined && {turnEndTimestamp: timestamp}),
        warPhaseStage: stage,
      };
    }

    case 'SET_TURN_END_TIMESTAMP': {
      const {timestamp} = action.payload;

      return {
        ...state,
        turnEndTimestamp: timestamp,
      };
    }

    // Автопилот ИИ промо-матча: включение или выключение персонального
    // времени хода (см. setPlayerTurnTimeOverride). Длительность — из
    // START_GAME, здесь она не меняется; игрока без персонального времени
    // экшен не трогает (создать его без длительности нельзя)
    case 'SET_PLAYER_TURN_TIME_OVERRIDE': {
      const {playerId, isActive} = action.payload || {};
      const override = state.playerTurnTimeOverrides?.[playerId];
      if (!override || override.isActive === Boolean(isActive)) {
        return state;
      }
      return {
        ...state,
        playerTurnTimeOverrides: {
          ...state.playerTurnTimeOverrides,
          [playerId]: {...override, isActive: Boolean(isActive)},
        },
      };
    }

    case 'ADD_CHAT_MESSAGE': {
      const {fromPlayerId, fromPlayerName, text, sentAt, clientMessageId, messageId} = action.payload;
      // Модерируемый чат ИИ-матча: одобренное сообщение приходит с id очереди
      // модерации (messageId) и id отправителя (clientMessageId). Они остаются
      // в состоянии — по снимку LOAD_STATE отправитель снимает свою строку «на
      // проверке» точно, а не по совпадению текста (см. matchChatReducer), и
      // повторная доставка того же сообщения его не задваивает. У обычного
      // чата id нет — сообщение пишется как раньше, из четырех полей
      const hasMessageId = typeof messageId === 'string' && messageId.length > 0;
      if (hasMessageId && state.chatMessages.some((message) => message.messageId === messageId)) {
        return state;
      }
      return {
        ...state,
        chatMessages: [...state.chatMessages, {
          fromPlayerId,
          fromPlayerName,
          text,
          sentAt,
          ...(hasMessageId ? {messageId} : {}),
          ...(typeof clientMessageId === 'string' && clientMessageId ? {clientMessageId} : {}),
        }],
      };
    }

    case 'APPLY_RECRUITMENT_ACTIONS': {
      // Применяем SIZE_RECRUITMENT действия к отрядам
      const sizeRecruitmentActions = state.pendingRecruitmentActions.filter(
        action => action.actionType === 'SIZE_RECRUITMENT'
      );

      const nextUnits = state.units.map(unit => {
        // Находим все SIZE_RECRUITMENT действия для этого отряда
        const sizeRecruitments = sizeRecruitmentActions.filter(
          action => action.unitId === unit.id
        );
        // Суммируем общее увеличение размера
        const totalSizeIncrease = sizeRecruitments.reduce((sum, action) => sum + (action.sizeAdded || 0), 0);
        if (totalSizeIncrease > 0) {
          return {
            ...unit,
            size: unit.size + totalSizeIncrease
          };
        }
        return unit;
      });

      return {
        ...state,
        units: nextUnits,
        pendingRecruitmentActions: [],
      };
    }

    case 'APPLY_MOVEMENT_ACTIONS': {
      // Находим отряды, которые использовали дополнительное снабжение для перемещения
      const unitsUsedAdditionalSupply = new Set();

      state.pendingMovementActions.forEach(action => {
        if (action.additionalSupply) {
          unitsUsedAdditionalSupply.add(action.unitId);
        }
      })

      // Сбрасываем additionalSupply у этих отрядов
      const nextUnits = state.units.map(unit => {
        if (unitsUsedAdditionalSupply.has(unit.id)) {
          return {
            ...unit,
            additionalSupply: false
          };
        }
        return unit;
      });

      // Не сбрасываем pendingMovementActions.
      // Так как перемещения учитываются в фазе стрельбы.

      // Убийства ловушками Гоблинов копились в pendingGoblinsTrapKillResults
      // по мере срабатывания в течение всего этапа перемещения (каждое —
      // отдельный немедленный диспатч, см. APPLY_GOBLINS_TRAP_MOVEMENT) —
      // здесь, строго на завершение этапа перемещения, переносим их одним
      // пакетом в общий combatResults, по тому же принципу, что
      // APPLY_SHOOTING_ACTIONS формирует combatResults для стрельбы на
      // завершении своего этапа (см. add-goblins-faction design.md
      // Decision 11) — это и есть момент, когда должна открыться общая
      // модалка результатов боя (modalsMiddleware реагирует на изменение
      // combatResults). ЗАМЕНЯЕТ combatResults, а не дописывает в конец - тот
      // же принцип, что и у APPLY_SHOOTING_ACTIONS/APPLY_MELEE_DAMAGE (оба
      // строят массив combatResults для СВОЕГО этапа с нуля и присваивают его
      // напрямую). combatResults чистится только локально (clearCombatResults -
      // noServerSync), поэтому клиент, не закрывший модалку результатов
      // ПРЕДЫДУЩЕГО этапа, увидел бы в ней смесь старых и новых записей при
      // дописывании (найдено внешним ревью ветки)
      const hasPendingTrapKillResults = state.pendingGoblinsTrapKillResults.length > 0;
      const nextCombatResults = hasPendingTrapKillResults
        ? state.pendingGoblinsTrapKillResults
        : state.combatResults;
      const nextCombatStats = hasPendingTrapKillResults
        ? buildTrapStageCombatStats(state.pendingGoblinsTrapKillResults, state.goblinsTrapStageTriggers, nextUnits, state.players)
        : state.combatStats;

      return {
        ...state,
        units: nextUnits,
        combatResults: nextCombatResults,
        combatStats: nextCombatStats,
        pendingGoblinsTrapKillResults: [],
        goblinsTrapStageTriggers: [],
      };
    }

    case 'SET_INVALID_PENDING_BUILDINGS': {
      return {
        ...state,
        invalidPendingBuildings: action.payload.invalidHexKeys
      };
    }

    // Обработка удаления отложенного строения
    case 'UNDO_PENDING_BUILDING': {
      const {hexKey, token, price, transferAgrarianismBenefit} = action.payload;
      const currentPlayer = state.players[state.currentPlayerIndex];

      const nextSpecialBuildings = {...state.specialBuildings};
      delete nextSpecialBuildings[hexKey];

      const undoneBuilding = state.pendingBuildings.find(b => b.hexKey === hexKey && b.token === token);

      // Удаляем строение из pendingBuildings
      let newPendingBuildings = state.pendingBuildings.filter(b => b.hexKey !== hexKey || b.token !== token);

      // «Первая ферма хода» при Аграризме дешевле и не тратит действие
      // строительства (см. getBuildingPrice). Если ее отменили вне очереди
      // (двойным кликом по ячейке, страховочной чисткой бота), а более поздняя
      // ферма этого хода осталась, льгота переходит к ближайшей из них — как
      // если бы отмененной фермы не было вовсе: переплата возвращается игроку,
      // действие освобождается. Только для экшенов с флагом (см. undoPendingBuilding)
      let agrarianismRefund = 0;
      if (transferAgrarianismBenefit && undoneBuilding?.isFreeAction && token === TOKENS.FARM
        && isPlayerPolicy(currentPlayer, POLICIES.AGRARIANISM)) {
        const heirIndex = newPendingBuildings.findIndex(b => b.type === CONSTRUCTION_ACTION_TYPES.CONSTRUCTION
          && b.token === TOKENS.FARM
          && !b.isFreeAction);
        if (heirIndex !== -1) {
          const heir = newPendingBuildings[heirIndex];
          agrarianismRefund = Math.max(0, heir.price - POLICY_AGRARIANISM_BUILDING_PRICE_FARM);
          newPendingBuildings = newPendingBuildings.map((building, index) => (index === heirIndex
            ? {...building, price: heir.price - agrarianismRefund, isFreeAction: true}
            : building));
        }
      }

      // Также удаляем из списка невалидных строений
      const newInvalidPendingBuildings = state.invalidPendingBuildings.filter(key => key !== hexKey);

      // Обновляем монеты и действия игрока
      const updatedPlayers = state.players.map((player, index) => {
        if (state.currentPlayerIndex === index) {
          return {
            ...player,
            coins: (player.coins || 0) + price + agrarianismRefund,
          };
        }
        return player;
      });

      const elementalsConstructionTokens = state.elementalsConstructionTokens;
      let elementIndex = null;

      if (isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS)) {
        const elementUsed = getElementForTokenType(token);

        if (elementUsed) {
          elementIndex = getLastUsedElementIndex(elementUsed, elementalsConstructionTokens);
        }
      }

      const nextElementalsConstructionTokens = elementalsConstructionTokens.map((element, index) => {
        return {
          ...element,
          isUsed: elementIndex === index ? false : element.isUsed,
        }
      })

      return {
        ...state,
        pendingBuildings: newPendingBuildings,
        specialBuildings: nextSpecialBuildings,
        invalidPendingBuildings: newInvalidPendingBuildings,
        players: updatedPlayers,
        elementalsConstructionTokens: nextElementalsConstructionTokens,
      };
    }

    case 'FACTION_LEPRECHAUNS_STEAL_COINS': {
      const {unitId, playerId, coinsStolen} = action.payload;
      const currentPlayer = state.players[state.currentPlayerIndex];

      const nextPlayers = state.players.map((player) => {
        if (player.id === playerId) {
          return {
            ...player,
            coins: player.coins - coinsStolen,
          }
        } else if (player.id === currentPlayer.id) {
          return {
            ...player,
            coins: player.coins + coinsStolen,
          }
        } else {
          return player
        }
      })

      return {
        ...state,
        players: nextPlayers,
        leprechaunsStealCoinsActions: [
          ...state.leprechaunsStealCoinsActions, {
            unitId,
            playerId,
            coinsStolen,
          }
        ],
      };
    }

    case 'FACTION_SUCCUBES_SEDUCE_UNIT': {
      const {unitId, targetUnitId, previousOwnerId, roll, attackerSize, targetSize, success} = action.payload;
      const currentPlayer = state.players[state.currentPlayerIndex];

      const seducerUnit = state.units.find(u => u.id === unitId);
      if (!seducerUnit) {
        console.warn('Cannot attempt seduction: unit not found');
        return state;
      }

      if (seducerUnit.hasUsedAbilityThisWarPhase) {
        console.warn('Cannot attempt seduction: unit already tried this war phase');
        return state;
      }

      if (seducerUnit.ownerId !== currentPlayer.id) {
        console.warn('Cannot attempt seduction: unit does not belong to current player');
        return state;
      }

      // Способность фракционная - см. calculateAvailableSeductionCells (единая
      // точка правды для UI), продублировано здесь на случай обхода UI
      // (сетевой рассинхрон, туториал, боты)
      if (!isUnitFaction(seducerUnit, FACTIONS.SUCCUBES)) {
        console.warn('Cannot attempt seduction: unit is not Succubes');
        return state;
      }

      // Катапульты (D4) не соблазняют - см. calculateAvailableSeductionCells
      if (seducerUnit.type === UNIT_TYPES.D4.id) {
        console.warn('Cannot attempt seduction: catapults cannot seduce');
        return state;
      }

      if (currentPlayer.coins < SUCCUBES_SEDUCTION_COST) {
        console.warn('Cannot attempt seduction: not enough coins');
        return state;
      }

      const nextUnits = state.units.map((unit) => {
        if (unit.id === unitId) {
          return {
            ...unit,
            hasUsedAbilityThisWarPhase: true,
          };
        }
        if (success && unit.id === targetUnitId) {
          // factionId, size, type и прочие свойства юнита-цели не меняются —
          // именно это сохраняет способности его исходной фракции после захвата.
          // capturedFromOwnerId фиксирует РЕАЛЬНОГО предыдущего владельца (не
          // фракцию) — рендер оверлея захвата в HexGridRenderer ищет игрока по
          // этому id, а не по совпадению factionId, иначе он путает игроков,
          // играющих за одну фракцию (см. ревью, дефект №13)
          return {
            ...unit,
            ownerId: currentPlayer.id,
            capturedFromOwnerId: previousOwnerId,
          };
        }
        return unit;
      });

      // Стоимость попытки списывается независимо от исхода (успех/провал)
      const nextPlayers = state.players.map((p) => {
        if (p.id === currentPlayer.id) {
          return {...p, coins: p.coins - SUCCUBES_SEDUCTION_COST};
        }
        return p;
      });

      return {
        ...state,
        units: nextUnits,
        players: nextPlayers,
        succubesSeductionActions: [
          ...state.succubesSeductionActions, {
            unitId,
            targetUnitId,
            previousOwnerId,
            roll,
            attackerSize,
            targetSize,
            success,
          }
        ],
      };
    }

    case 'FACTION_GOBLINS_SET_TRAP': {
      const {unitId, trapId, hexKey, roll, success} = action.payload;
      const currentPlayer = state.players[state.currentPlayerIndex];

      const trapUnit = state.units.find(u => u.id === unitId);
      if (!trapUnit) {
        console.warn('Cannot set trap: unit not found');
        return state;
      }
      if (trapUnit.hasUsedAbilityThisWarPhase) {
        console.warn('Cannot set trap: unit already used ability this war phase');
        return state;
      }
      if (trapUnit.ownerId !== currentPlayer.id) {
        console.warn('Cannot set trap: unit does not belong to current player');
        return state;
      }
      if (!isUnitFaction(trapUnit, FACTIONS.GOBLINS)) {
        console.warn('Cannot set trap: unit is not Goblins');
        return state;
      }
      if (trapUnit.size < GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY) {
        console.warn('Cannot set trap: unit too small');
        return state;
      }
      if (currentPlayer.coins < GOBLINS_TRAP_SETUP_COST) {
        console.warn('Cannot set trap: not enough coins');
        return state;
      }

      const nextUnits = state.units.map(unit =>
        unit.id === unitId ? {...unit, hasUsedAbilityThisWarPhase: true} : unit
      );

      // Стоимость попытки списывается независимо от исхода (успех/провал)
      const nextPlayers = state.players.map(p =>
        p.id === currentPlayer.id ? {...p, coins: p.coins - GOBLINS_TRAP_SETUP_COST} : p
      );

      const nextGoblinsTraps = success
        ? [...state.goblinsTraps, {id: trapId, ownerId: currentPlayer.id, hexKey}]
        : state.goblinsTraps;

      return {
        ...state,
        units: nextUnits,
        players: nextPlayers,
        goblinsTraps: nextGoblinsTraps,
        goblinsAbilityActions: [
          ...state.goblinsAbilityActions,
          {type: 'set_trap', unitId, hexKey, roll, success}
        ],
      };
    }

    case 'FACTION_GOBLINS_REMOVE_TRAP': {
      const {unitId, trapId} = action.payload;
      const currentPlayer = state.players[state.currentPlayerIndex];

      const removingUnit = state.units.find(u => u.id === unitId);
      if (!removingUnit) {
        console.warn('Cannot remove trap: unit not found');
        return state;
      }
      if (removingUnit.hasUsedAbilityThisWarPhase) {
        console.warn('Cannot remove trap: unit already used ability this war phase');
        return state;
      }
      if (removingUnit.ownerId !== currentPlayer.id) {
        console.warn('Cannot remove trap: unit does not belong to current player');
        return state;
      }
      if (!isUnitFaction(removingUnit, FACTIONS.GOBLINS)) {
        console.warn('Cannot remove trap: unit is not Goblins');
        return state;
      }
      if (removingUnit.size < GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY) {
        console.warn('Cannot remove trap: unit too small');
        return state;
      }

      const trap = state.goblinsTraps.find(t => t.id === trapId);
      if (!trap || trap.ownerId !== currentPlayer.id) {
        console.warn('Cannot remove trap: trap not found or not owned by current player');
        return state;
      }
      if (getHexDistance(removingUnit.hexKey, trap.hexKey) > GOBLINS_ABILITY_RADIUS) {
        console.warn('Cannot remove trap: out of range');
        return state;
      }

      const nextUnits = state.units.map(unit =>
        unit.id === unitId ? {...unit, hasUsedAbilityThisWarPhase: true} : unit
      );
      const nextPlayers = state.players.map(p =>
        p.id === currentPlayer.id ? {...p, coins: p.coins + GOBLINS_TRAP_REMOVAL_REWARD} : p
      );

      return {
        ...state,
        units: nextUnits,
        players: nextPlayers,
        goblinsTraps: state.goblinsTraps.filter(t => t.id !== trapId),
        goblinsAbilityActions: [
          ...state.goblinsAbilityActions,
          {type: 'remove_trap', unitId, hexKey: trap.hexKey}
        ],
      };
    }

    // FACTION_GOBLINS_DAMAGE_BUILDING обрабатывается в index.js (кросс-срезовый
    // кейс, как и APPLY_SHOOTING_ACTIONS) - разрушение строения требует
    // одновременного доступа к state.hexes (перевод в дорогу) и
    // state.gameState (citiesCache/winPoints/buildingsDamage), которых нет в
    // этом per-slice редьюсере (см. design.md Decision 17: сюда его добавили
    // по ошибке при первой реализации - урон применялся, но state.hexes
    // никогда реально не менялся, т.к. этот редьюсер отвечает только за срез
    // gameState, а "hexes" в возвращаемом объекте был мёртвым полем)

    case 'APPLY_GOBLINS_TRAP_MOVEMENT': {
      const {
        unitId, finalHexKey, unitDied, remainingSize, killerPlayerId, triggeredTrapEvents, removedTrapIds,
        usesAdditionalSupply, isManeuver
      } = action.payload;

      const movedUnit = state.units.find(u => u.id === unitId);
      if (!movedUnit) {
        console.warn('Cannot apply goblins trap movement: unit not found');
        return state;
      }

      let nextUnits;
      let nextPendingKillResults = state.pendingGoblinsTrapKillResults;
      // Защитно: состояния из старых записей матчей и собранные вручную в
      // тестах поля не имеют
      const prevStageTriggers = state.goblinsTrapStageTriggers || [];
      let nextPlayers = state.players;
      let nextPendingMovementActions = state.pendingMovementActions;
      let nextPendingManeuverActions = state.pendingManeuverActions;

      if (unitDied) {
        nextUnits = state.units.filter(unit => unit.id !== unitId);

        if (killerPlayerId) {
          const killerPlayer = getPlayerById(state.players, killerPlayerId);
          const unitConfig = getUnitTypeConfig(movedUnit.type);
          const winPoints = calcWinPointsForKill(killerPlayer, unitConfig, movedUnit.factionId);

          nextPlayers = state.players.map(p =>
            p.id === killerPlayerId ? {...p, winPoints: p.winPoints + winPoints} : p
          );

          nextPendingKillResults = [
            ...state.pendingGoblinsTrapKillResults,
            {killedUnit: movedUnit, killerPlayerId, winPoints, combatType: COMBAT_TYPE.TRAP}
          ];
        }
      } else {
        // Юнит выжил - сбрасываем additionalSupply здесь же, если он был
        // использован для этого хода, чтобы не потерять сброс, который иначе
        // происходит в APPLY_MOVEMENT_ACTIONS на основе pendingMovementActions
        nextUnits = state.units.map(unit =>
          unit.id === unitId
            ? {
              ...unit,
              hexKey: finalHexKey,
              size: remainingSize,
              ...(usesAdditionalSupply ? {additionalSupply: false} : {}),
            }
            : unit
        );

        // Запись в pendingMovementActions/pendingManeuverActions ВСЁ ЖЕ нужна -
        // без неё decideMovementStep.js/decideManeuverStep.js (фильтр "отряд
        // ещё не двигался/не маневрировал в этом этапе") не видели бы, что
        // отряд уже сходил, и на следующем тике выбирали бы ЕГО ЖЕ повторно -
        // отряд бота совершал второе, лишнее действие сразу после срабатывания
        // ловушки (найдено пользователем в ручном тестировании для
        // перемещения; манёвр - тот же класс бага). Помечена
        // trapTriggered: true, чтобы остаться необратимой (design.md
        // Decision 8) - findUndoableUnitMove (используется и для перемещения,
        // и для манёвра)/UndoButton.js/tryUndoUnitManeuver её игнорируют.
        // isManeuver в payload различает, в какой из двух списков писать -
        // резолвер маршрута общий для перемещения и манёвра
        // (resolveMovementWithTraps), а бухгалтерия "отряд уже действовал в
        // этом этапе" у них разная
        if (isManeuver) {
          nextPendingManeuverActions = [
            ...state.pendingManeuverActions,
            {
              unitId,
              fromHexKey: movedUnit.hexKey,
              toHexKey: finalHexKey,
              trapTriggered: true,
            }
          ];
        } else {
          nextPendingMovementActions = [
            ...state.pendingMovementActions,
            {
              unitId,
              fromHexKey: movedUnit.hexKey,
              toHexKey: finalHexKey,
              additionalSupply: Boolean(usesAdditionalSupply),
              trapTriggered: true,
            }
          ];
        }
      }

      const removedTrapIdsSet = new Set(removedTrapIds || []);
      const nextGoblinsTraps = state.goblinsTraps.filter(trap => !removedTrapIdsSet.has(trap.id));

      return {
        ...state,
        units: nextUnits,
        players: nextPlayers,
        goblinsTraps: nextGoblinsTraps,
        pendingGoblinsTrapKillResults: nextPendingKillResults,
        goblinsTrapStageTriggers: [
          ...prevStageTriggers,
          {
            unitId,
            // Снимок ДО урона: по нему рисуется призрак погибшего и
            // «-N» под выжившим (см. updateGoblinsTrapTriggerDisplay)
            unit: {
              id: movedUnit.id,
              type: movedUnit.type,
              ownerId: movedUnit.ownerId,
              size: movedUnit.size,
              factionId: movedUnit.factionId,
              capturedFromOwnerId: movedUnit.capturedFromOwnerId ?? null,
            },
            died: unitDied,
            finalHexKey,
            totalDamageTaken: Math.max(0, movedUnit.size - remainingSize),
            triggeredTrapEvents: triggeredTrapEvents || [],
          },
        ],
        pendingMovementActions: nextPendingMovementActions,
        pendingManeuverActions: nextPendingManeuverActions,
        goblinsAbilityActions: [
          ...state.goblinsAbilityActions,
          {type: 'trap_triggered', unitId, finalHexKey, unitDied, triggeredTrapEvents}
        ],
      };
    }

    case 'ELEMENTALS_SET_RANDOM_ELEMENTS': {
      const {tokens} = action.payload;

      return {
        ...state,
        elementalsConstructionTokens: tokens,
      }
    }

    // Жетоны элементов планирующего Элементаля (см. elementalsPlanTokensByPlayerId).
    // Первый бросок окончательный: повторный для того же игрока не принимается.
    // Если ход игрока уже наступил (действие дошло позже передачи хода), жетоны
    // сразу становятся жетонами хода — пока тот их еще не вытянул
    case 'ELEMENTALS_SET_PLAN_TOKENS': {
      if (isStalePhaseAction(action, state)) return state;
      const {playerId, tokens} = action.payload || {};
      if (state.phase !== PHASES.CONSTRUCTION || !playerId || !Array.isArray(tokens)) {
        return state;
      }
      const player = (state.players || []).find((candidate) => candidate.id === playerId);
      if (!player || !isPlayerFaction(player, FACTIONS.ELEMENTALS)
        || (state.phaseCompletedPlayers || []).includes(playerId)
      ) {
        return state;
      }
      const planTokensByPlayerId = state.elementalsPlanTokensByPlayerId || {};
      if (planTokensByPlayerId[playerId]) {
        return state;
      }
      if (state.players[state.currentPlayerIndex]?.id === playerId) {
        return (state.elementalsConstructionTokens || []).length === 0
          ? {...state, elementalsConstructionTokens: tokens}
          : state;
      }
      return {
        ...state,
        elementalsPlanTokensByPlayerId: {...planTokensByPlayerId, [playerId]: tokens},
      };
    }

    case 'ELEMENTALS_SPEND_ELEMENT': {
      const {elementIndex} = action.payload;
      const nextElementalsConstructionTokens = state.elementalsConstructionTokens.map((element, index) => {
        return {
          ...element,
          isUsed: index === elementIndex ? true : element.isUsed,
        }
      });

      return {
        ...state,
        elementalsConstructionTokens: nextElementalsConstructionTokens,
      }
    }

    case 'SET_POLICY_MARKET': {
      const {policyMarket} = action.payload;

      return {
        ...state,
        policyMarket,
      }
    }

    case 'CLEAR_POLICY_MARKET': {
      const {policyMarket} = action.payload;
      const pendingBuildings = state.pendingBuildings;
      const nextInitiativePlayerId = state.nextInitiativePlayerId;
      const players = state.players;
      const currentPlayer = state.players[state.currentPlayerIndex];
      const isInitiativeInterceptedWithIsolationism = state.isInitiativeInterceptedWithIsolationism;

      if (currentPlayer.isClearedPolicyMarketThisTurn) {
        console.error('[CLEAR_POLICY_MARKET] player has already cleared policy market:', currentPlayer.id);
      }
      if (!checkIfHasRemainingConstructionActions(currentPlayer, pendingBuildings, nextInitiativePlayerId, isInitiativeInterceptedWithIsolationism)) {
        console.error('[CLEAR_POLICY_MARKET] player has no actions to clear policy market:', currentPlayer.id);
      }

      const nextPlayers = players.map((player) => {
        if (player.id === currentPlayer.id) {
          return {
            ...player,
            isClearedPolicyMarketThisTurn: true,
          }
        } else {
          return player;
        }
      })

      return {
        ...state,
        policyMarket,
        players: nextPlayers,
      }
    }

    case 'ADD_NEW_POLICY_TO_POLICY_MARKET': {
      const {policy} = action.payload;
      const policyMarket = state.policyMarket;

      const nextPolicyMarket = [...policyMarket, policy]

      return {
        ...state,
        policyMarket: nextPolicyMarket,
      }
    }

    case 'BUY_POLICY': {
      const {index} = action.payload;

      const policyMarket = state.policyMarket;
      const players = state.players;
      const currentPlayer = state.players[state.currentPlayerIndex];

      const price = calcPolicyPrice(index, currentPlayer);

      if (currentPlayer.coins < price) {
        console.warn(`[BUY_POLICY] not enough coins: ${currentPlayer.id}`);
        return state;
      }

      const policy = policyMarket[index];

      const nextPlayers = players.map((player) => {
        if (player.id === currentPlayer.id) {
          const coins = player.coins - price;

          let nextConstructionPhaseActions = player.constructionPhaseActions;
          if (isPlayerFaction(player, FACTIONS.HUMANS)) {
            if (policy.id === POLICIES.INDUSTRIALISM) {
              nextConstructionPhaseActions += POLICY_INDUSTRIALISM_CONSTRUCTION_ACTIONS_BONUS;
            } else if (player.policy && player.policy.id === POLICIES.INDUSTRIALISM) {
              nextConstructionPhaseActions -= POLICY_INDUSTRIALISM_CONSTRUCTION_ACTIONS_BONUS;
            }

            return {
              ...player,
              coins,
              policy,
              nextPolicy: policy,
              constructionPhaseActions: nextConstructionPhaseActions,
            }
          } else {
            if (!player.policy && policy.id === POLICIES.INDUSTRIALISM) {
              nextConstructionPhaseActions += POLICY_INDUSTRIALISM_CONSTRUCTION_ACTIONS_BONUS;
            }
            return {
              ...player,
              coins,
              policy: player.policy ? player.policy : policy,
              nextPolicy: policy,
              constructionPhaseActions: nextConstructionPhaseActions,
            }
          }
        } else {
          return player;
        }
      })

      const nextPolicyMarket = getArrayWithoutIndex(policyMarket, index);

      return {
        ...state,
        players: nextPlayers,
        policyMarket: nextPolicyMarket,
      }
    }

    case 'GIANTS_TOGGLE_AUTO_ADDITIONAL_SUPPLY': {
      const actorId = resolveSupplyActorId(state, action.payload);
      const currentPlayer = state.players.find((player) => player.id === actorId);
      if (!currentPlayer) {
        return state;
      }

      const nextPlayers = state.players.map((player) => {
        if (player.id === currentPlayer.id) {
          return {
            ...player,
            isGiantsAutoAdditionalSupplyOn: !player.isGiantsAutoAdditionalSupplyOn,
          }
        } else {
          return player;
        }
      });

      return {
        ...state,
        players: nextPlayers,
      }
    }

    case 'POLICY_FANATICISM_CONVERT_WIN_POINTS': {
      const players = state.players;
      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }
      const currentPlayer = state.players.find((player) => player.id === actorId);
      if (!currentPlayer) {
        return state;
      }

      // Те же условия, что у кнопки обмена и у бота: раз в раунд и только при
      // хватающих очках. Раньше редьюсер лишь писал ошибку и все равно вычитал —
      // дубль экшена (двойной клик, повторная доставка) увел бы очки в минус.
      // В записях матчей таких обменов не было: исход их не меняется
      if (currentPlayer.isFanaticismConversionUsed) {
        return state;
      }
      if (currentPlayer.winPoints < POLICY_FANATICISM_WIN_POINTS_CONVERSION) {
        console.error('[POLICY_FANATICISM_CONVERT_WIN_POINTS] player has not enough win points for conversion:', currentPlayer.id);
        return state;
      }

      const nextPlayers = players.map((player) => {
        if (player.id === currentPlayer.id) {
          return {
            ...player,
            winPoints: player.winPoints - POLICY_FANATICISM_WIN_POINTS_CONVERSION,
            coins: player.coins + POLICY_FANATICISM_COINS_CONVERSION,
            isFanaticismConversionUsed: true,
          }
        } else {
          return player;
        }
      });

      return {
        ...state,
        players: nextPlayers,
      }
    }

    case 'POLICY_AGRARIANISM_COMMIT_CONVERSION': {
      const {selectedFarmsForConversion} = action.payload;

      const actorId = resolveSupplyActorId(state, action.payload);
      if (checkIfSupplyActorLocked(state, actorId)) {
        return state;
      }

      const nextPendingSupply = [...state.pendingSupply]

      const {coins, usedSupplyCount} = calcAgrarianismConversion(selectedFarmsForConversion);

      for (let i = 0; i < usedSupplyCount; i++) {
        nextPendingSupply.push({
          farmHexKey: selectedFarmsForConversion[i],
          isUsedForAgrarianismConversion: true,
          isNotUndoable: true,
          ...(action.payload?.playerId && {playerId: actorId}),
        })
      }

      const players = state.players;
      const currentPlayer = state.players.find((player) => player.id === actorId);
      if (!currentPlayer) {
        return state;
      }

      const nextPlayers = players.map((player) => {
        if (player.id === currentPlayer.id) {
          return {
            ...player,
            coins: player.coins + coins,
          }
        } else {
          return player;
        }
      });

      return {
        ...state,
        players: nextPlayers,
        pendingSupply: nextPendingSupply,
      }
    }

    case 'POLICY_ISOLATIONISM_INTERCEPT_INITIATIVE': {
      const currentPlayer = state.players[state.currentPlayerIndex];

      // Вторая дверь к той же инициативе: бесплатный перехват при покупке
      // Изоляционизма. Правила у него те же, кроме цены (действие не тратится),
      // а проверок тут не было вообще — предложение гейтила только модалка
      // покупки политики (PolicyMarketSideMenu)
      const isolationismRejection = getInitiativeActionRejection(state, action);
      if (isolationismRejection && state.gameType !== GAME_TYPES.REPLAY) {
        console.warn('[POLICY_ISOLATIONISM_INTERCEPT_INITIATIVE] rejected:', isolationismRejection, currentPlayer.id);
        return state;
      }

      return {
        ...state,
        nextInitiativePlayerId: currentPlayer.id,
        isInitiativeInterceptedWithIsolationism: true
      };
    }

    case 'SET_MATCH_IS_RANKED':
      return {...state, isRanked: Boolean(action.payload.isRanked)};

    // Досрочный конец партии итогами текущего раунда (сервер: все живые
    // игроки покинули обычный сетевой матч). Итоги — те же, что на обычном
    // конце, но по состоянию на этот момент, без дохода конца раунда; сыгранных
    // раундов — текущий. Повторный экшен и уже подведенные итоги ничего не меняют
    case 'END_GAME_AT_CURRENT_ROUND': {
      if (!state.isStarted || (state.gameResults && state.gameResults.length > 0)) {
        return state;
      }
      return {
        ...state,
        gameResults: buildGameResults(state.players, state.citiesCache || {}),
        finalRound: state.round,
        gameEndReason: action.payload?.reason || null,
        gameEndRound: state.round,
      };
    }

    case 'SET_EXTENDED_GAME_RESULTS': {
      return {
        ...state,
        extendedGameResults: action.payload,
      };
    }

    default:
      return state;
  }
};

export default sharedGameStateReducer;
